import { Injectable } from '@angular/core';
import { Client, createInstance, UserAttributes } from '@optimizely/optimizely-sdk';
import { environment } from '../../../environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '~services/auth/auth.service';
import { FeatureFlags } from './feature-flags.enum';

@Injectable({
  providedIn: 'root',
})
export class OptimizelyService {
  private optimizelyInstance: Client;

  constructor(private cookieService: CookieService, private authService: AuthService) {
    this.optimizelyInstance = createInstance({
      sdkKey: environment.optimizely.sdkKey,
      logLevel: environment.optimizely.logLevel,
    });
  }

  async isFeatureEnabled(
    featureKey: FeatureFlags,
    userAttributes?: UserAttributes,
  ): Promise<boolean> {
    let isEnabled = false;

    try {
      const id = this.getOrCreateUserId();
      const isOptimizelyReady = await this.optimizelyInstance.onReady();

      if (!isOptimizelyReady.success) {
        throw new Error(isOptimizelyReady.reason);
      }

      const cohort = this.cohort();

      const user = this.optimizelyInstance.createUserContext(id, { ...userAttributes, cohort });

      if (!user) {
        throw new Error('Failed to create user context');
      }

      const decision = user.decide(featureKey);

      isEnabled = decision.enabled;

      this.optimizelyInstance.close();
    } catch (error) {
      window.zbm?.reportError(error);
    }

    return isEnabled;
  }

  private getOrCreateUserId(): string {
    const cookieName = 'giftCardExperimentUserId';

    const setUserIdToCookie = (userId: string) => {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 365);

      this.cookieService.set(cookieName, userId, expiryDate);
    };

    if (this.cookieService.check(cookieName)) {
      const savedUserId = this.cookieService.get(cookieName);
      if (this.authService.userId && savedUserId !== this.authService.userId) {
        this.cookieService.delete(cookieName);
        setUserIdToCookie(this.authService.userId);
        return this.authService.userId;
      }

      return savedUserId;
    }

    const userId = this.authService.userId || uuidv4();
    setUserIdToCookie(userId);

    return userId;
  }

  private createCohort(cookieName: string): number {
    const cohort = Math.floor(Math.random() * 10) + 1;
    const expiration = new Date();
    expiration.setFullYear(new Date().getFullYear() + 1);

    this.cookieService.set(cookieName, cohort.toString(), expiration);

    return cohort;
  }

  private cohort(): number {
    const cohortCookieName = 'giftCardCohort';

    const cookieCohort = this.cookieService.get(cohortCookieName);

    if (cookieCohort) {
      const existingCohort = Number(cookieCohort);

      if (isNaN(existingCohort) || existingCohort === 0 || existingCohort > 10) {
        const cohort = this.createCohort(cohortCookieName);
        return cohort;
      }

      return existingCohort;
    }

    const cohort = this.createCohort(cohortCookieName);
    return cohort;
  }
}
